import { LogOut } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Consistent logout control used across ALL role portals (CSA / Manager / Dealer / Setup).
 * Compact icon-only variant on mobile, icon + label on md+.
 *
 * data-testid: `logout-${where}` — pass a page identifier so tests can target each header.
 */
export default function LogoutButton({ where = "generic", variant = "ghost", size = "sm", className = "" }) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const doLogout = async () => {
    setPending(true);
    try {
      await logout();
      // Hard-navigate to /login so the router leaves any role-specific page.
      // Prevents a subsequent Dealer login from landing on a stale /csa route.
      nav("/login", { replace: true });
    } finally {
      setPending(false);
      setOpen(false);
    }
  };
  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        className={"gap-1.5 " + className}
        data-testid={`logout-${where}`}
        title={t("components.logoutbutton.log_out")}
      >
        <LogOut className="h-4 w-4" />
        <span className="hidden md:inline">{t("common.logout")}</span>
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="logout-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("components.logoutbutton.log_out_of_hisabx")}</AlertDialogTitle>
            <AlertDialogDescription>
              You'll need to sign in again to access your shift, cashbook and reports.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="logout-cancel" disabled={pending}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doLogout} disabled={pending} data-testid="logout-confirm-btn">
              {pending ? "Logging out…" : "Yes, log out"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
